import { createContext, useContext, useState, type ReactNode } from "react";

export type ToolId =
  | "merge"
  | "split"
  | "watermark"
  | "pdf-to-image"
  | "pdf-to-word"
  | "pdf-to-html"
  | "compress"
  | "crop"
  | "remove-pages"
  | "page-numbers"
  | "inspector"
  | "edit-page-size"
  | "edit-margins"
  | "edit-contrast"
  | "edit-pdf"
  | "encrypt"
  | "decrypt"
  | "image-to-pdf"
  | "word-to-pdf"
  | "markdown-to-pdf"
  | "extract-form-data"
  | "ocr-pdf"
  | "pdf-to-zip"
  | "redact-text"
  | "mail-merge"
  | "batch-stamp"
  | "bates-numbering"
  | "dynamic-stamp"
  | "header-footer"
  | "edit-pdf-layout"
  | "increase-pdf-size"
  | "enhance-pdf"
  | "flatten-pdf"
  | "rearrange"
  | "rotate"
  | "flatten"
  | "page-numbers"
  | "extract-text"
  | "pdf-to-image"
  | "redact"
  | "link-manager"
  | "extract-text-ocr"
  | "create-pdf"
  | "form-builder"
  | "pdf-diff"
  | "pdf-to-excel"
  | "sign-fill"
  | "pdf-translator"
  | "html-to-pdf"
  | "pdf-repair"
  | "pdf-to-pptx"
  | "page-extractor"
  | "pdf-metadata"
  | "excel-to-pdf"
  | "add-bookmarks"
  | "pdf-permissions"
  | "pdf-sanitizer"
  | "invoice-generator"
  | "qrcode-generator"
  | "csv-to-pdf"
  | "epub-to-pdf"
  | "pdf-to-epub"
  | "pdf-to-webp"
  | "webp-to-pdf"
  | "svg-to-pdf"
  | "extract-images"
  | "split-by-size";

export interface ToolHistoryEntry {
  id: string;
  toolId: ToolId;
  toolName: string;
  files: string[];
  status: "success" | "error";
  createdAt: Date;
  errorMessage?: string;
  technicalDetails?: string;
}

interface ToolHistoryContextValue {
  entries: ToolHistoryEntry[];
  addEntry: (entry: Omit<ToolHistoryEntry, "id" | "createdAt">) => void;
  clear: () => void;
}

const ToolHistoryContext = createContext<ToolHistoryContextValue | undefined>(
  undefined,
);

export const ToolHistoryProvider = ({ children }: { children: ReactNode }) => {
  const [entries, setEntries] = useState<ToolHistoryEntry[]>([]);

  const addEntry: ToolHistoryContextValue["addEntry"] = (entry) => {
    const full: ToolHistoryEntry = {
      ...entry,
      id: `${entry.toolId}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      createdAt: new Date(),
    };
    setEntries((prev) => [full, ...prev].slice(0, 50));
  };

  const clear = () => setEntries([]);

  return (
    <ToolHistoryContext.Provider value={{ entries, addEntry, clear }}>
      {children}
    </ToolHistoryContext.Provider>
  );
};

export const useToolHistory = () => {
  const ctx = useContext(ToolHistoryContext);
  if (!ctx)
    throw new Error("useToolHistory must be used within ToolHistoryProvider");
  return ctx;
};
